import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SystemSettings {
  maintenance_mode?: boolean;
  delivery_distance?: number;
  phone_verification?: boolean;
  ads_enabled?: boolean;
}

export function useSystemSettings() {
  const [settings, setSettings] = useState<SystemSettings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const parseBool = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase().trim() === 'true';
    if (value !== null && value !== undefined) return Boolean(value);
    return false;
  };

  const fetchSettings = async (force = false) => {
    try {
      if (!force) setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .order('key');

      if (error) throw error;

      const settingsMap: SystemSettings = {
        maintenance_mode: false,
        delivery_distance: 10,
        phone_verification: false,
        ads_enabled: false
      };

      if (data && data.length > 0) {
        data.forEach((setting) => {
          if (setting.key === 'maintenance_mode') {
            settingsMap.maintenance_mode = parseBool(setting.value);
          } else if (setting.key === 'max_delivery_distance') {
            settingsMap.delivery_distance = typeof setting.value === 'number' 
              ? setting.value 
              : Number(setting.value) || 10;
          } else if (setting.key === 'require_phone_verification') {
            settingsMap.phone_verification = parseBool(setting.value);
          } else if (setting.key === 'ads_enabled') {
            settingsMap.ads_enabled = parseBool(setting.value);
          }
        });
      }

      setSettings(settingsMap);
      setLastFetch(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar configurações');
      setSettings({
        maintenance_mode: false,
        delivery_distance: 10,
        phone_verification: false,
        ads_enabled: false
      });
    } finally {
      if (!force) setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return { 
    settings, 
    loading, 
    error, 
    refetch: fetchSettings,
    lastFetch 
  };
}
